// RigSentinel Owner Dashboard V1 — read-only account detail drawer.
// Every value here is the SAME data already shown in the users table; this
// just gives the owner a focused, single-account view. No mutation actions.
import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fetchUserDetail } from "@/lib/ownerDashboardApi";

function fmtDate(iso) {
  if (!iso || iso === "Not available") return iso || "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[var(--rs-border)] text-[12px]">
      <span className="text-[var(--rs-text-3)]">{label}</span>
      <span className="text-[var(--rs-text)] font-mono-rs text-right">{value ?? "—"}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] label-up text-[var(--rs-amber)] mb-1">{title}</div>
      {children}
    </div>
  );
}

export default function OwnerUserDrawer({ userId, open, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    setData(null);
    setError("");
    fetchUserDetail(userId).then(setData).catch((e) => setError(e.message));
  }, [open, userId]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent data-testid="owner-user-drawer" className="bg-[var(--rs-surface)] border-[var(--rs-border)] overflow-y-auto w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-[var(--rs-text)]" data-testid="owner-drawer-email">{data?.account?.email || "Loading…"}</SheetTitle>
          <SheetDescription className="text-[var(--rs-text-3)]">{userId}</SheetDescription>
        </SheetHeader>

        {error && <p className="text-[12px] text-[var(--rs-danger)] mt-3" data-testid="owner-drawer-error">{error}</p>}
        {!error && !data && <p className="text-[12px] text-[var(--rs-text-3)] mt-3">Loading…</p>}

        {data && (
          <>
            <Section title="Account">
              <Row label="Created" value={fmtDate(data.account.accountCreated)} />
              <Row label="First login" value={fmtDate(data.account.firstLogin)} />
              <Row label="Last login" value={fmtDate(data.account.lastLogin)} />
              <Row label="Last active" value={fmtDate(data.account.lastActive)} />
            </Section>

            <Section title="Entitlement">
              <Row label="Effective access" value={data.entitlement.effectiveAccess} />
              <Row label="Reason" value={data.entitlement.entitlementReason} />
              <Row label="Plan" value={data.entitlement.plan} />
              <Row label="Billing cadence" value={data.entitlement.billingCadence} />
              <Row label="Founding 500" value={data.entitlement.founding500 ? "Yes" : "No"} />
              <Row label="Paddle status" value={data.entitlement.paddleStatus} />
              <Row label="Trial start" value={fmtDate(data.entitlement.trialStart)} />
              <Row label="Trial end" value={fmtDate(data.entitlement.trialEnd)} />
              <Row label="Current period start" value={fmtDate(data.entitlement.currentPeriodStart)} />
              <Row label="Current period end" value={fmtDate(data.entitlement.currentPeriodEnd)} />
              <Row label="Cancel at period end" value={data.entitlement.cancelAtPeriodEnd ? "Yes" : "No"} />
            </Section>

            <Section title="Commercial">
              <Row label="Paddle customer ID" value={data.commercial.paddleCustomerId} />
              <Row label="Paddle subscription ID" value={data.commercial.paddleSubscriptionId} />
              <Row label="Last payment status" value={data.commercial.lastPaymentStatus} />
              <Row label="Last payment date" value={fmtDate(data.commercial.lastPaymentDate)} />
              <Row label="Last payment amount" value={data.commercial.lastPaymentAmount != null ? `${data.commercial.lastPaymentAmount} ${data.commercial.lastPaymentCurrency || ""}` : "—"} />
            </Section>

            <Section title="Non-sensitive usage">
              <Row label="Sessions recorded" value={data.usage.sessionsRecorded} />
              <Row label="Last feature activity" value={fmtDate(data.usage.lastFeatureActivity)} />
              <Row label="Calculator uses" value={data.usage.calculatorUses} />
              <Row label="Premium feature attempts" value={data.usage.premiumFeatureAttempts} />
              <Row label="Upgrade modal views" value={data.usage.upgradeViews} />
              <Row label="Checkout attempts" value={data.usage.checkoutAttempts} />
              {Array.isArray(data.usage.topFeatureCategories) && data.usage.topFeatureCategories.length > 0 && (
                <div className="mt-2 text-[11px] text-[var(--rs-text-3)]">
                  Top categories: {data.usage.topFeatureCategories.map((c) => `${c.category} (${c.count})`).join(", ")}
                </div>
              )}
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
